/**
 * Product form - intercepts the standard `{% form 'product' %}` submit
 * and POSTs to /cart/add.js instead of doing a full page reload.
 *
 * On success, dispatches CartAddEvent so the cart drawer opens automatically.
 *
 * @see https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-add-js
 */

import { Component } from "@theme/component";
import { CartAddEvent, CartErrorEvent } from "@theme/events";

class ProductFormComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    const form = this.querySelector("form");
    if (form) form.addEventListener("submit", this.#handleSubmit);
  }

  /**
   * @param {SubmitEvent} event
   */
  #handleSubmit = async (event) => {
    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const submitter = /** @type {HTMLButtonElement | null} */ (event.submitter);

    // Don't intercept the dynamic checkout button (Shop Pay, etc.) - let it
    // submit normally so the customer goes straight to checkout.
    if (submitter?.name === "checkout") return;

    event.preventDefault();
    submitter?.setAttribute("disabled", "");

    try {
      const formData = new FormData(form);
      formData.append("sections_url", window.location.pathname);

      // Line item properties (ex. gravure) : on envoie la valeur nettoyée, et
      // rien du tout si elle est vide, pour ne pas créer de ligne panier
      // distincte portant une propriété vide.
      for (const [key, value] of [...formData.entries()]) {
        if (!key.startsWith("properties[") || typeof value !== "string") continue;
        const trimmed = value.trim();
        if (trimmed) formData.set(key, trimmed);
        else formData.delete(key);
      }

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { Accept: "application/javascript" },
        body: formData,
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.description || body.message || `HTTP ${response.status}`);
      }

      // Tell the rest of the page that the cart changed.
      document.dispatchEvent(new CartAddEvent());
    } catch (error) {
      console.error("[product-form] add-to-cart failed:", error);
      document.dispatchEvent(new CartErrorEvent("product-form", error.message));
    } finally {
      submitter?.removeAttribute("disabled");
    }
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}
